import type { V2Answers, V2BranchRule, V2Page } from '../../types/V2Types';
import { evaluateCondition } from './showIfEvaluator';

export interface BranchDecision {
	/** The next page id, or null when the current page is terminal. */
	nextPageId: string | null;
	/** The rule that produced the routing decision, or null if the default fired. */
	matchedRule: V2BranchRule | null;
}

/**
 * Pure routing function: given the just-submitted page and its answers, decide
 * what page id comes next. First matching `branchRules` entry wins, with
 * fallthrough to `defaultNextPageId`. Returns `nextPageId: null` when the page
 * has neither a matching rule nor a default — interpret that as "the BE will
 * tell us done:true on submit".
 *
 * This is client-side only; the BE makes the authoritative routing call. The
 * client copy exists so renderers can show "Next" vs "Finish" labels and so
 * Cypress can deterministically follow the flow.
 */
export function evaluateBranch(page: V2Page, answers: V2Answers): BranchDecision {
	for (const rule of page.branchRules ?? []) {
		if (evaluateCondition(rule, answers)) {
			return { nextPageId: rule.nextPageId, matchedRule: rule };
		}
	}
	return {
		nextPageId: page.defaultNextPageId ?? null,
		matchedRule: null,
	};
}

/** Convenience: is this page the last one given current answers? */
export function isTerminal(page: V2Page, answers: V2Answers): boolean {
	return evaluateBranch(page, answers).nextPageId === null;
}
